import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Button from '../components/Button';
import Input from '../components/Input';
import LocationPicker from '../components/LocationPicker';
import { useAuth } from '../context/AuthContext';
import { 
  getXperienceMedia, 
  updateXperienceMedia, 
  createXperienceMedia,
  deleteXperienceMedia,
  uploadXperienceMediaPoster,
  uploadXperienceMediaFile,
  getXperience,
  getXperienceTypes,
  getXperienceFormats,
  getXperienceCategories,
  XperienceMedia,
  LookupItem
} from '../services/api';
import { t } from '../i18n';
import './XperienceMediaDetail.css';

const LAST_MEDIA_LOCATION_KEY = 'xmedia_last_location';

const XperienceMediaDetailPage: React.FC = () => {
  const { id, xperienceId } = useParams<{ id?: string, xperienceId?: string }>();
  const navigate = useNavigate();
  const { creator } = useAuth();
  const isNew = Boolean(xperienceId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [xperienceMedia, setXperienceMedia] = useState<XperienceMedia | null>(null);
  const [posterUploadProgress, setPosterUploadProgress] = useState(0);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);

  const [types, setTypes] = useState<LookupItem[]>([]);
  const [formats, setFormats] = useState<LookupItem[]>([]);
  const [categories, setCategories] = useState<LookupItem[]>([]);
  const [xperiencePrimaryLocation, setXperiencePrimaryLocation] = useState<{ longitude: string; latitude: string } | null>(null);
  const [lastMediaLocation, setLastMediaLocation] = useState<{ longitude: string; latitude: string } | null>(null);
  const [locationSource, setLocationSource] = useState<'xperience' | 'last' | ''>('');

  // Refs para archivos locales antes de subir
  const selectedFileRef = useRef<File | null>(null);
  const selectedPosterRef = useRef<File | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: '1',
    format: '1',
    category: '1',
    url: '',
    posterUrl: '',
    orden: '',
    runtime: '0',
    longitude: '',
    latitude: '',
    needToBeDownloaded: '0',
    isPublic: '1',
    isPremium: '0',
    credits: '0',
    hide: '0',
    createdDate: ''
  });

  const [posterPreview, setPosterPreview] = useState('');

  useEffect(() => {
    loadData();
  }, [id, xperienceId]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_MEDIA_LOCATION_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { longitude?: string; latitude?: string };
      if (!parsed.longitude || !parsed.latitude) return;
      setLastMediaLocation({
        longitude: String(parsed.longitude),
        latitude: String(parsed.latitude)
      });
    } catch {
      // Ignore malformed local storage value.
    }
  }, []);

  const extractXperiencePrimaryLocation = (xperience: any) => {
    const longitude = xperience?.primaryLocarion?.coordinates?.[0] ?? xperience?.primaryLongitude ?? '';
    const latitude = xperience?.primaryLocarion?.coordinates?.[1] ?? xperience?.primaryLatitude ?? '';

    if (longitude === '' || latitude === '') return null;

    return {
      longitude: String(longitude),
      latitude: String(latitude)
    };
  };

  const formatCoordPreview = (value: string) => {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return value;
    return parsed.toFixed(6);
  };

  const loadData = async () => {
    try {
      const [typesResponse, formatsResponse, categoriesResponse] = await Promise.all([
        getXperienceTypes(),
        getXperienceFormats(),
        getXperienceCategories(),
      ]);

      setTypes(typesResponse.data.types);
      setFormats(formatsResponse.data.formats);
      setCategories(categoriesResponse.data.categories);

      if (isNew && xperienceId) {
        // En creación: Auto-calcular orden y pre-poblar form
        const xpResp = await getXperience(xperienceId);
        setXperiencePrimaryLocation(extractXperiencePrimaryLocation(xpResp.data.xperience));
        const existingLinks = xpResp.data.xperience.mediaLinks || [];
        const nextOrder = existingLinks.length + 1;
        setFormData(prev => ({ ...prev, orden: String(nextOrder) }));
        setLoading(false);
        return;
      }

      if (id) {
        // Edición
        const mediaResponse = await getXperienceMedia(id);
        const media = mediaResponse.data.xperienceMedia;
        setXperienceMedia(media);
        const longitude = media.location?.coordinates?.[0] ?? media.longitude ?? '';
        const latitude = media.location?.coordinates?.[1] ?? media.latitude ?? '';
        setFormData({
          title: media.title || '',
          description: media.description || '',
          type: media.type !== undefined && media.type !== null ? String(media.type) : '1',
          format: media.format !== undefined && media.format !== null ? String(media.format) : '1',
          category: media.category !== undefined && media.category !== null ? String(media.category) : '1',
          url: media.url || '',
          posterUrl: media.posterUrl || '',
          orden: media.order !== undefined && media.order !== null ? String(media.order) : (media.orden !== undefined && media.orden !== null ? String(media.orden) : ''),
          runtime: media.runtime !== undefined && media.runtime !== null ? String(media.runtime) : '',
          longitude: longitude !== '' ? String(longitude) : '',
          latitude: latitude !== '' ? String(latitude) : '',
          needToBeDownloaded: media.needToBeDownloaded !== undefined && media.needToBeDownloaded !== null ? String(media.needToBeDownloaded) : '0',
          isPublic: media.isPublic !== undefined && media.isPublic !== null ? String(media.isPublic) : '1',
          isPremium: media.isPremium !== undefined && media.isPremium !== null ? String(media.isPremium) : '0',
          credits: media.credits !== undefined && media.credits !== null ? String(media.credits) : '',
          hide: media.hide !== undefined && media.hide !== null ? String(media.hide) : '0',
          createdDate: media.createdDate ? new Date(media.createdDate).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''
        });
        if (media.posterUrl) {
          setPosterPreview(`https://x-perienc.es/${media.posterUrl}`);
        }

        if (media.xperience) {
          const xpResp = await getXperience(String(media.xperience));
          setXperiencePrimaryLocation(extractXperiencePrimaryLocation(xpResp.data.xperience));
        }
      }
    } catch (err: any) {
      setError(t('xmedia.error.loadData'));
    } finally {
      setLoading(false);
    }
  };

  const handlePosterSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      selectedPosterRef.current = file;
      setFormData({ ...formData, posterUrl: `[Local selected: ${file.name}]` });
      setPosterPreview(URL.createObjectURL(file));
      setSuccess(t('xmedia.success.posterSelected'));
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleFileSelectLocal = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      selectedFileRef.current = file;
      setFormData({ ...formData, url: `[Local selected: ${file.name}]` });
      setSuccess(t('xmedia.success.mediaSelected'));
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (window.confirm(t('xmedia.confirm.deleteMessage'))) {
      setSaving(true);
      setError('');
      try {
        await deleteXperienceMedia(id);
        const targetXpId = xperienceMedia?.xperience || xperienceId;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSuccess(`✓ ${t('xmedia.success.deleted')}`);
        setTimeout(() => {
          if (targetXpId) {
            navigate(`/xperience/${targetXpId}`);
          } else {
            navigate(-1);
          }
        }, 1500);
      } catch (err: any) {
        setError(t('xmedia.error.delete'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSaving(false);
      }
    }
  };

  const handleCopyXperienceLocation = () => {
    if (!xperiencePrimaryLocation) {
      setError(t('xmedia.error.noXperienceLocation'));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      longitude: xperiencePrimaryLocation.longitude,
      latitude: xperiencePrimaryLocation.latitude
    }));
    setLocationSource('xperience');
    setError('');
    setSuccess(t('xmedia.success.locationCopied'));
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleCopyLastLocation = () => {
    if (!lastMediaLocation) {
      setError(t('xmedia.error.noLastLocation'));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      longitude: lastMediaLocation.longitude,
      latitude: lastMediaLocation.latitude
    }));
    setLocationSource('last');
    setError('');
    setSuccess(t('xmedia.success.lastLocationCopied'));
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const missingFields: string[] = [];
    if (!formData.title.trim()) missingFields.push(t('xmedia.field.title'));
    if (!formData.description.trim()) missingFields.push(t('xmedia.field.description'));
    if (!formData.type) missingFields.push(t('xmedia.field.type'));
    if (!formData.format) missingFields.push(t('xmedia.field.format'));
    if (!formData.category) missingFields.push(t('xmedia.field.category'));
    if (!formData.url.trim() && !selectedFileRef.current) missingFields.push(t('xmedia.field.mediaFile'));
    if (!formData.posterUrl.trim() && !selectedPosterRef.current) missingFields.push(t('xmedia.field.posterUrl'));
    if (formData.orden === '') missingFields.push(t('xmedia.field.order'));
    if (formData.runtime === '') missingFields.push(t('xmedia.field.runtime'));
    if (formData.credits === '') missingFields.push(t('xmedia.field.credits'));

    if (missingFields.length > 0) {
      setError(`${t('xmedia.error.requiredPrefix')} ${missingFields.join(', ')}.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    let currentId = id;
    let targetXpId = xperienceMedia?.xperience || xperienceId;

    const parseNumber = (value: string) => {
      if (value.trim() === '') return undefined;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? undefined : parsed;
    };

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        type: parseNumber(formData.type),
        format: parseNumber(formData.format),
        category: parseNumber(formData.category),
        orden: parseNumber(formData.orden),
        runtime: parseNumber(formData.runtime),
        longitude: parseNumber(formData.longitude),
        latitude: parseNumber(formData.latitude),
        needToBeDownloaded: parseNumber(formData.needToBeDownloaded),
        isPublic: parseNumber(formData.isPublic),
        isPremium: parseNumber(formData.isPremium),
        credits: parseNumber(formData.credits),
        hide: parseNumber(formData.hide),
        xperience: targetXpId
      };

      if (isNew) {
        const resp = await createXperienceMedia(payload);
        currentId = resp.data.xperienceMedia.id;
      } else {
        await updateXperienceMedia(id!, payload);
      }

      if (currentId && selectedPosterRef.current) {
        setPosterUploadProgress(1);
        await uploadXperienceMediaPoster(currentId, selectedPosterRef.current, setPosterUploadProgress);
      }

      if (currentId && selectedFileRef.current) {
        setFileUploadProgress(1);
        await uploadXperienceMediaFile(currentId, selectedFileRef.current, setFileUploadProgress);
      }

      if (formData.longitude.trim() !== '' && formData.latitude.trim() !== '') {
        const lastLocation = {
          longitude: formData.longitude,
          latitude: formData.latitude
        };
        localStorage.setItem(LAST_MEDIA_LOCATION_KEY, JSON.stringify(lastLocation));
        setLastMediaLocation(lastLocation);
      }
      
      setSuccess(isNew ? `✓ ${t('xmedia.success.created')}` : `✓ ${t('xmedia.success.updated')}`);
      
      // Hacemos scroll arriba
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Y en unos segundos volvemos a la xperience
      setTimeout(() => {
        navigate(`/xperience/${targetXpId}`);
      }, 3000);

    } catch (err: any) {
      setError(t('xmedia.error.save'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
      setPosterUploadProgress(0);
      setFileUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <div className="xmedia-page">
        <Navbar />
        <div className="xmedia-loading">{t('xmedia.loading')}</div>
      </div>
    );
  }

  if (!isNew && !xperienceMedia) {
    return (
      <div className="xmedia-page">
        <Navbar />
        <div className="xmedia-error">{t('xmedia.notFound')}</div>
      </div>
    );
  }

  const buildMediaUrl = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || trimmed.startsWith('[')) return '';
    const cleanedPath = trimmed.replace(/^\/+/, '');
    return `https://x-perienc.es/${cleanedPath}`;
  };

  return (
    <div className="xmedia-page">
      <Navbar />

      <div className="xmedia-container">
        <button
          className="xmedia-back"
          onClick={() => navigate(-1)}
        >
          ← {t('xmedia.back')}
        </button>

        {error && <div className="xmedia-error">{error}</div>}
        {success && <div className="xmedia-success">{success}</div>}

        <div className="xmedia-card">
          <div className="xmedia-header">
            <div>
              <h1 className="xmedia-title">{isNew ? t('xmedia.title.create') : t('xmedia.title.edit')}</h1>
              <p className="xmedia-subtitle">{t('xmedia.subtitle')}</p>
            </div>
            {posterPreview && (
              <div
                className="xmedia-poster"
                style={{ backgroundImage: `url(${posterPreview})` }}
              />
            )}
          </div>

          <form onSubmit={handleSave} className="xmedia-form">
            <div className="xmedia-fields">
              <div className="xmedia-field">
                <label className="xmedia-label">{t('xmedia.field.title')} *</label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('xmedia.placeholder.title')}
                  required
                />
              </div>

              <div className="xmedia-field">
                <label className="xmedia-label">{t('xmedia.field.description')} *</label>
                <div className="clearable-field clearable-field-textarea">
                  <textarea
                    className="xmedia-textarea clearable-textarea"
                    name="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t('xmedia.placeholder.description')}
                    rows={4}
                    required
                  />
                  {formData.description && (
                    <button
                      type="button"
                      className="clearable-field-button"
                      onClick={() => setFormData({ ...formData, description: '' })}
                      aria-label={t('common.clearSearch')}
                      title={t('common.clearSearch')}
                    >
                      x
                    </button>
                  )}
                </div>
              </div>

              <div className="xmedia-row">
                <div className="xmedia-field">
                  <label className="xmedia-label">{t('xmedia.field.type')} *</label>
                  <select
                    className="xmedia-select"
                    name="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                  >
                    {types.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="xmedia-field">
                  <label className="xmedia-label">{t('xmedia.field.format')} *</label>
                  <select
                    className="xmedia-select"
                    name="format"
                    value={formData.format}
                    onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                    required
                  >
                    {formats.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
                <div className="xmedia-field">
                  <label className="xmedia-label">{t('xmedia.field.category')} *</label>
                  <select
                    className="xmedia-select"
                    name="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="xmedia-field">
                <label className="xmedia-label">{t('xmedia.field.mediaFile')} *</label>
                <div className="xmedia-file-upload">
                  <Input
                    name="url"
                    value={formData.url}
                    readOnly
                    placeholder={t('xmedia.placeholder.mediaUrl')}
                  />
                  <div className="xmedia-upload-wrapper">
                    <input
                      type="file"
                      id="file-upload"
                      className="xmedia-file-input"
                      onChange={handleFileSelectLocal}
                    />
                    <button
                      type="button"
                      className="xmedia-upload-btn"
                      onClick={() => document.getElementById('file-upload')?.click()}
                    >
                      {isNew ? t('xmedia.selectFile') : t('xmedia.changeFile')}
                    </button>
                    {!isNew && !formData.url.startsWith('[') && (
                      <button
                        type="button"
                        className="xmedia-upload-btn"
                        disabled={!formData.url.trim()}
                        onClick={() => {
                          const targetUrl = buildMediaUrl(formData.url);
                          if (targetUrl) {
                            window.open(targetUrl, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        {t('xmedia.viewMedia')}
                      </button>
                    )}
                  </div>
                  {fileUploadProgress > 0 && fileUploadProgress < 100 && (
                    <div className="xmedia-progress">
                      <div className="xmedia-progress-bar" style={{ width: `${fileUploadProgress}%` }} />
                      <span>{Math.round(fileUploadProgress)}%</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="xmedia-field">
                <label className="xmedia-label">{t('xmedia.field.posterUrl')} ({t('xmedia.thumbnail')}) *</label>
                <div className="xmedia-file-upload">
                  <Input
                    name="posterUrl"
                    value={formData.posterUrl}
                    readOnly
                    placeholder={t('xmedia.placeholder.posterUrl')}
                  />
                  <div className="xmedia-upload-wrapper">
                    <input
                      type="file"
                      id="poster-upload"
                      className="xmedia-file-input"
                      accept="image/*"
                      onChange={handlePosterSelect}
                    />
                    <button
                      type="button"
                      className="xmedia-upload-btn"
                      onClick={() => document.getElementById('poster-upload')?.click()}
                    >
                      {isNew ? t('xmedia.selectImage') : t('xmedia.changeImage')}
                    </button>
                  </div>
                  {posterUploadProgress > 0 && posterUploadProgress < 100 && (
                    <div className="xmedia-progress">
                      <div className="xmedia-progress-bar" style={{ width: `${posterUploadProgress}%` }} />
                      <span>{Math.round(posterUploadProgress)}%</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="xmedia-field">
                <label className="xmedia-label">{t('xmedia.field.order')} *</label>
                <Input
                  name="orden"
                  type="number"
                  value={formData.orden}
                  onChange={(e) => setFormData({ ...formData, orden: e.target.value })}
                  placeholder={t('xmedia.placeholder.order')}
                  readOnly={!isNew && false}
                  required
                />
              </div>

              <div className="xmedia-row">
                <div className="xmedia-field">
                  <label className="xmedia-label">{t('xmedia.field.runtime')} ({t('xmedia.seconds')}) *</label>
                  <Input
                    name="runtime"
                    type="number"
                    value={formData.runtime}
                    onChange={(e) => setFormData({ ...formData, runtime: e.target.value })}
                    placeholder={t('xmedia.placeholder.runtime')}
                    required
                  />
                </div>
                <div className="xmedia-field">
                  <label className="xmedia-label">{t('xmedia.field.credits')} *</label>
                  <Input
                    name="credits"
                    type="number"
                    value={formData.credits}
                    onChange={(e) => setFormData({ ...formData, credits: e.target.value })}
                    placeholder={t('xmedia.placeholder.credits')}
                    required
                  />
                </div>
              </div>

              <div className="xmedia-field">
                <label className="xmedia-label">{t('xmedia.field.location')}</label>
                <LocationPicker
                  longitude={formData.longitude}
                  latitude={formData.latitude}
                  onChange={({ longitude, latitude }) => {
                    setLocationSource('');
                    setFormData({ ...formData, longitude, latitude });
                  }}
                />
                <div className="location-picker-actions" style={{ marginTop: '8px' }}>
                  <button
                    type="button"
                    className="location-picker-action-btn"
                    onClick={handleCopyXperienceLocation}
                    disabled={!xperiencePrimaryLocation}
                  >
                    {t('xmedia.copyXperienceLocation')}
                  </button>
                  <button
                    type="button"
                    className="location-picker-action-btn"
                    onClick={handleCopyLastLocation}
                    disabled={!lastMediaLocation}
                  >
                    {t('xmedia.copyLastLocation')}
                  </button>
                </div>
                {locationSource === 'xperience' && (
                  <div className="xmedia-location-badge">{t('xmedia.locationSource.xperience')}</div>
                )}
                {locationSource === 'last' && (
                  <div className="xmedia-location-badge">{t('xmedia.locationSource.last')}</div>
                )}
                {lastMediaLocation && (
                  <div className="xmedia-location-last-preview">
                    {t('xmedia.lastLocationPreview')}: {formatCoordPreview(lastMediaLocation.longitude)}, {formatCoordPreview(lastMediaLocation.latitude)}
                  </div>
                )}
              </div>

              {!isNew && formData.createdDate && (
                <div className="xmedia-row">
                  <div className="xmedia-field" style={{ flex: 1 }}>
                    <label className="xmedia-label">{t('xmedia.field.createdDate')}</label>
                    <div style={{ padding: '12px', background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-secondary)' }}>
                      {formData.createdDate}
                    </div>
                  </div>
                </div>
              )}

              <div className="xmedia-row">
                <div className="xmedia-field xmedia-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.needToBeDownloaded === '1'}
                      onChange={(e) => setFormData({ ...formData, needToBeDownloaded: e.target.checked ? '1' : '0' })}
                    />
                    {t('xmedia.requiresDownload')}
                  </label>
                </div>
                <div className="xmedia-field xmedia-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isPublic === '1'}
                      onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked ? '1' : '0' })}
                    />
                    {t('xmedia.isPublic')}
                  </label>
                </div>
                <div className="xmedia-field xmedia-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isPremium === '1'}
                      onChange={(e) => setFormData({ ...formData, isPremium: e.target.checked ? '1' : '0' })}
                    />
                    {t('xmedia.isPremium')}
                  </label>
                </div>
                {creator?.role === 'administrator' && (
                  <div className="xmedia-field xmedia-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.hide === '1'}
                        onChange={(e) => setFormData({ ...formData, hide: e.target.checked ? '1' : '0' })}
                      />
                      {t('xmedia.hide')}
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="xmedia-actions" style={{ justifyContent: !isNew ? 'space-between' : 'flex-end' }}>
              {!isNew && (
                <Button 
                  type="button" 
                  variant="primary" 
                  style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: 'white' }}
                  onClick={handleDelete}
                  disabled={saving}
                >
                  {t('xmedia.deleteMedia')}
                </Button>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button type="button" variant="secondary" onClick={() => navigate(-1)} disabled={saving}>
                  {t('xmedia.cancel')}
                </Button>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? t('xmedia.saving') : t('xmedia.saveChanges')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default XperienceMediaDetailPage;
